import { defineStore } from 'pinia';
import { fetchAppSummary, setAvailability } from '@/api/home';
import { ApiError, type AsyncState } from '@/core';
import type { TechAppSummary } from '@/domain/technician';

interface HomeStoreState {
  summary: AsyncState<TechAppSummary>;
}

export const useHomeStore = defineStore('home', {
  state: (): HomeStoreState => ({ summary: { kind: 'idle' } }),
  actions: {
    async load(): Promise<void> {
      this.summary = { kind: 'loading' };
      try {
        const value = await fetchAppSummary();
        this.summary = { kind: 'data', value };
      } catch (cause) {
        // Erros de domínio (ex.: sessão sem técnico vinculado) não podem estourar como
        // rejeição não tratada no setup do shell — viram estado de erro com retry, como
        // qualquer falha de API. Regra: a tela decide o que fazer com o estado.
        const error = cause instanceof ApiError
          ? cause
          : new ApiError({
              status: 401,
              correlationId: undefined,
              kind: 'http',
              message: 'Sua sessão expirou. Entre novamente.',
            });
        this.summary = { kind: 'error', error };
      }
    },
    /** Toggle de disponibilidade: otimista no dado local, revertido se o PUT falhar. */
    async toggleAvailability(): Promise<void> {
      if (this.summary.kind !== 'data') return;
      const previous = this.summary.value.isAvailable;
      const next = !previous;
      this.summary = { kind: 'data', value: { ...this.summary.value, isAvailable: next } };
      try {
        await setAvailability(next);
      } catch (cause) {
        this.summary = { kind: 'data', value: { ...this.summary.value, isAvailable: previous } };
        throw cause;
      }
    },
  },
});
